import { getRouteIdFromPathname, RouteId, routes } from '@trello/routes';

import { getWorkspaceStateForBoard } from './getters/getWorkspaceStateForBoard';
import { getWorkspaceStateForCard } from './getters/getWorkspaceStateForCard';
import { getWorkspaceStateForOrganization } from './getters/getWorkspaceStateForOrganization';
import { WorkspaceState } from './workspaceState';

interface WorkspaceStateGetter {
  (...args: string[]): Promise<WorkspaceState>;
}

export function getGlobalWorkspaceState(
  params: string,
): Promise<WorkspaceState> {
  return Promise.resolve({
    status: 'READY',
    error: null,
    isGlobal: true,
    idWorkspace: null,
  });
}

const getNoMatchingRouteErrorWorkspaceState = (): Promise<WorkspaceState> =>
  Promise.resolve({
    status: 'ERROR',
    error: 'NO_MATCHING_ROUTE',
    isGlobal: false,
    idWorkspace: null,
  });

export const workspaceStateGetterMap: Record<RouteId, WorkspaceStateGetter> = {
  [RouteId.ACCOUNT]: getWorkspaceStateForOrganization,
  [RouteId.BILLING]: getWorkspaceStateForOrganization,
  [RouteId.BILLING_CARD]: getWorkspaceStateForOrganization,
  [RouteId.BILLING_CARD_PRODUCT]: getWorkspaceStateForOrganization,
  [RouteId.BOARD]: getWorkspaceStateForBoard,
  [RouteId.BOARD_DASHBOARD]: getWorkspaceStateForBoard,

  [RouteId.BOARD_OLD]: (path: string) => {
    const parts = path.split('/');
    const idBoard = parts.length > 1 ? parts[1] : parts[0];
    return getWorkspaceStateForBoard(idBoard);
  },

  [RouteId.BOARD_REFERRAL]: getWorkspaceStateForBoard,
  [RouteId.CARD]: getWorkspaceStateForCard,

  [RouteId.CARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getWorkspaceStateForBoard(idBoard);
  },

  [RouteId.CARD_AND_BOARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getWorkspaceStateForBoard(idBoard);
  },

  [RouteId.MEMBER_CARDS_FOR_ORG]: getWorkspaceStateForOrganization,
  [RouteId.MEMBER_HOME_BOARDS]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_BY_ID]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_EXPORT]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_FREE_TRIAL]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_GUESTS]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_MEMBER_CARDS]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_MEMBERS]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_POWER_UPS]: getWorkspaceStateForOrganization,
  [RouteId.ORGANIZATION_TABLES]: getWorkspaceStateForOrganization,
  [RouteId.PROFILE]: getWorkspaceStateForOrganization,
  [RouteId.TEAM_GETTING_STARTED]: getWorkspaceStateForOrganization,
  [RouteId.TEAM_HIGHLIGHTS]: getWorkspaceStateForOrganization,
  [RouteId.TEAM_REPORTS]: getWorkspaceStateForOrganization,
  [RouteId.USER_OR_ORG]: getWorkspaceStateForOrganization,

  [RouteId.BLANK]: getGlobalWorkspaceState,
  [RouteId.CREATE_FIRST_BOARD]: getGlobalWorkspaceState,
  [RouteId.CREATE_FIRST_TEAM]: getGlobalWorkspaceState,
  [RouteId.DOUBLE_SLASH]: getGlobalWorkspaceState,
  [RouteId.GO]: getGlobalWorkspaceState,
  [RouteId.INVITE_ACCEPT_BOARD]: getGlobalWorkspaceState,
  [RouteId.INVITE_ACCEPT_TEAM]: getGlobalWorkspaceState,

  [RouteId.ENTERPRISE_ADMIN]: getWorkspaceStateForOrganization,
  [RouteId.ENTERPRISE_ADMIN_TAB]: getWorkspaceStateForOrganization,

  [RouteId.MEMBER_ACTIVITY]: getWorkspaceStateForOrganization,
  [RouteId.MEMBER_ALL_BOARDS]: getWorkspaceStateForOrganization,
  [RouteId.MEMBER_CARDS]: getWorkspaceStateForOrganization,
  [RouteId.MEMBER_HOME]: getGlobalWorkspaceState,
  [RouteId.MEMBER_TASKS]: getWorkspaceStateForOrganization,

  [RouteId.POWER_UP_ADMIN]: getGlobalWorkspaceState,
  [RouteId.POWER_UP_EDIT]: getGlobalWorkspaceState,
  [RouteId.POWER_UP_PUBLIC_DIRECTORY]: getGlobalWorkspaceState,

  [RouteId.PROFILE]: getWorkspaceStateForOrganization,

  [RouteId.SEARCH]: getGlobalWorkspaceState,

  [RouteId.SELECT_ORG_TO_UPGRADE]: getGlobalWorkspaceState,
  [RouteId.SELECT_TEAM_TO_UPGRADE]: getGlobalWorkspaceState,

  [RouteId.SHORTCUTS]: getGlobalWorkspaceState,
  [RouteId.SHORTCUTS_OVERLAY]: getGlobalWorkspaceState,

  [RouteId.TEMPLATES_SUBMIT]: getGlobalWorkspaceState,
  [RouteId.TEMPLATES]: getGlobalWorkspaceState,
  [RouteId.TEMPLATES_RECOMMEND]: getGlobalWorkspaceState,

  [RouteId.TO]: getGlobalWorkspaceState,
  [RouteId.WELCOME]: getGlobalWorkspaceState,

  [RouteId.ERROR_PAGE]: getNoMatchingRouteErrorWorkspaceState,
};

export function getWorkspaceStateFromPathname(
  pathname: string,
): Promise<WorkspaceState> {
  const routeId = getRouteIdFromPathname(pathname);

  if (!routeId) {
    return getNoMatchingRouteErrorWorkspaceState();
  }

  const getter = workspaceStateGetterMap[routeId];

  const match = routes[routeId].regExp.exec(pathname.slice(1));
  const params = match?.slice(1) ?? [];
  return getter(...params);
}
